'use client';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import { CART_STORAGE_KEY, makeStore, type RootState } from './store';
import { setCartData } from './actions/user.action';

/**
 * `initialLocation` is read from the request cookie by the server layout and
 * handed down, so the server's first paint and the client's first paint agree
 * on the city. Without it the header rendered the default on the server and
 * the cookie's value on the client, and React discarded the hydrated tree.
 */
export default function ReduxProvider({
  children,
  initialLocation,
}: {
  children: React.ReactNode;
  initialLocation?: RootState['location'] | null;
}) {
  // useState, not a module constant: the factory must run once per store, and
  // during SSR a module constant would be shared across every request.
  const [store] = useState(() =>
    makeStore(initialLocation ? { location: initialLocation } : undefined),
  );

  // Restore a persisted cart strictly client-side, AFTER hydration, so it can
  // never diverge from what the server rendered. See store.ts.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(CART_STORAGE_KEY);
      const cart = raw ? JSON.parse(raw) : null;
      if (cart) {
        store.dispatch(
          setCartData(
            cart.selectedDate,
            cart.selectedTime,
            cart.selectedTickets,
            cart.eventId,
          ),
        );
      }
    } catch {
      // sessionStorage can throw (private browsing, quota) - skip restore.
    }
  }, [store]);

  return <Provider store={store}>{children}</Provider>;
}
